import type { ReactNode } from "react";
import type { HourPoint, ResultHour, Scenario } from "../sim/types";
import { computeMonthlySavings } from "../sim/monthly-savings";

interface WinterInsightProps {
  baselineHours: HourPoint[];
  resultHours: ResultHour[];
  scenario: Scenario;
  onScrollToInvestments: () => void;
}

/**
 * Visar insikten om att vintern står för stor andel av elimporten men
 * batteriet sparar lite där.
 *
 * Beräkning av besparing per månad delas med MonthlySavingsChart via
 * sim/monthly-savings — det garanterar att de inte kan diverga (om grafen
 * visar +50 kr en månad ser denna komponent samma siffra).
 *
 * Vintermånader = december, januari, februari.
 */
export default function WinterInsightCard({
  baselineHours,
  resultHours,
  scenario,
  onScrollToInvestments,
}: WinterInsightProps) {
  // Vintermånadernas andel av total elimport (oberoende av batteri-besparing)
  let winterImport = 0;
  let totalImport = 0;
  for (const h of baselineHours) {
    const month = new Date(h.from).getMonth();
    const isWinter = month === 11 || month === 0 || month === 1;
    totalImport += h.importKWh;
    if (isWinter) winterImport += h.importKWh;
  }
  const winterImportShare = totalImport > 0 ? winterImport / totalImport : 0;

  // Besparing per månad — exakt samma data som MonthlySavingsChart visar
  const monthly = computeMonthlySavings(baselineHours, resultHours, scenario);
  const winterSavingsKr = monthly
    .filter(
      (m) => m.monthIndex === 11 || m.monthIndex === 0 || m.monthIndex === 1,
    )
    .reduce((s, m) => s + m.savingsSek, 0);
  const totalSavingsKr = monthly.reduce((s, m) => s + m.savingsSek, 0);

  const winterSavingsShare =
    totalSavingsKr > 0 ? winterSavingsKr / totalSavingsKr : 0;

  // Trigga om vintern står för betydligt högre andel av importen
  // än vad batteriet faktiskt sparar där.
  const shouldShow =
    winterImportShare > 0.35 && winterSavingsShare < 0.25 && totalSavingsKr > 0;

  if (!shouldShow) return null;

  const winterKr = Math.round(winterSavingsKr);
  const winterKrAbs = new Intl.NumberFormat("sv-SE").format(Math.abs(winterKr));

  // Formulera olika beroende på om batteriet gör vinst eller förlust på vintern.
  // Förlust kan ske om spotpriserna är så platta att batteriets RTE-förluster
  // äter upp arbitragen — typiskt i ren-elvärmda hus utan sol i januari.
  let savingsDescription: ReactNode;
  if (winterSavingsKr < 0) {
    savingsDescription = (
      <>
        batteriet går ut med <strong>{winterKrAbs} kr i förlust</strong>{" "}
        där. Laddförlusterna äter upp den lilla arbitrage som finns när
        solen är borta
      </>
    );
  } else if (winterSavingsShare < 0.05) {
    savingsDescription = (
      <>
        batteriet sparar bara <strong>{winterKrAbs} kr</strong> där
        (under 5% av årets totala besparing)
      </>
    );
  } else {
    savingsDescription = (
      <>
        batteriet sparar bara{" "}
        <strong>{Math.round(winterSavingsShare * 100)}%</strong> av sin
        totala besparing där ({winterKrAbs} kr)
      </>
    );
  }

  return (
    <div className="winter-insight-card">
      <div className="winter-insight-icon" aria-hidden="true">
        ❄
      </div>
      <div className="winter-insight-content">
        <h4 className="winter-insight-title">
          Hög vinterförbrukning, låg vinterbesparing
        </h4>
        <p className="winter-insight-text">
          Vintermånaderna (dec-feb) står för{" "}
          <strong>{Math.round(winterImportShare * 100)}%</strong> av din
          årliga elimport, men {savingsDescription}. Detta är fysiskt
          logiskt: utan sol finns mindre att arbitragera.
        </p>
        <p className="winter-insight-text">
          Det betyder också att åtgärder som minskar din vinterförbrukning
          (isolering, värmepump-uppgradering, kompletterande värmekälla)
          påverkar batteriets återbetalning väldigt lite. De två
          investeringarna konkurrerar inte med varandra utan kompletterar.
        </p>
        <button
          type="button"
          className="winter-insight-cta"
          onClick={onScrollToInvestments}
        >
          Modellera scenario med andra åtgärder →
        </button>
      </div>
    </div>
  );
}
